import { GraphQLClient } from 'graphql-request';

import { SHOPIFY_API_VERSION } from '../constants';
import { isBrowser } from '../utils';
import {
  customerAccessTokenCreateMutation,
  customerAccessTokenRenewMutation,
  customerActivateMutation,
  customerAddressCreateMutation,
  customerAddressDeleteMutation,
  customerAddressSetDefaultMutation,
  customerAddressUpdateMutation,
  customerCreateMutation,
  customerQuery,
  customerRecoverMutation,
  customerResetMutation,
} from './shopify-queries';

const client = new GraphQLClient(
  `https://${process.env.GATSBY_SHOPIFY_SHOP_NAME}.myshopify.com/api/${SHOPIFY_API_VERSION}/graphql`,
  {
    headers: {
      Accept: 'application/json',
      'X-Shopify-Storefront-Access-Token':
        process.env.GATSBY_SHOPIFY_ACCESS_TOKEN,
    },
  }
);

/**
 * Returns up to 100 products, of each having up to 10 variants, using the
 * Shopify GraphQL API because `shopify-buy` only returns the first 20 products
 */
export const getAllProducts = async () => {
  const data = await client.request(`
    query {
      products(first: 100) {
        edges {
          node {
            id
            handle
            availableForSale
            variants(first: 100) {
              edges {
                node {
                  id
                  availableForSale
                }
              }
            }
          }
        }
      }
    }
  `);

  return data;
};

/**
 * Saves the user access token in local storage
 *
 * @param {Object} user
 */
export const setUser = (user) => {
  window.localStorage.setItem('shopify_access_token', JSON.stringify(user));
};

/**
 * Returns the user access token from local storage. If the access token is
 * expired to to expire in the next 30 days, renew the token.
 *
 * @returns {Object} user access token
 */
export const getUser = () => {
  const accessToken =
    isBrowser && window.localStorage.getItem('shopify_access_token')
      ? JSON.parse(window.localStorage.getItem('shopify_access_token'))
      : {};

  if (accessToken.expiresAt) {
    const now = new Date();
    const expiresAt = new Date(accessToken.expiresAt);
    const renewAt = new Date(accessToken.expiresAt);
    renewAt.setDate(expiresAt.getDate() - 30);

    if (expiresAt < now) {
      setUser({});
      return {};
    }
    if (renewAt < now) {
      client
        .request(customerAccessTokenRenewMutation, {
          customerAccessToken: accessToken.accessToken,
        })
        .then(({ customerAccessTokenRenew }) => {
          if (
            customerAccessTokenRenew &&
            customerAccessTokenRenew.customerAccessToken
          ) {
            setUser(customerAccessTokenRenew.customerAccessToken);
          }
        });
    }
  }

  return accessToken;
};

/**
 * Returns true if a Shopify customer is logged in by looking for an access
 * token
 *
 * @returns {boolean}
 */
export const isLoggedIn = () => !!getUser().accessToken;

/**
 * Logs in a Shopify customer by returning a valid access token on success
 *
 * @param {Object} param
 * @param {string} param.email
 * @param {string} param.password
 * @returns {Promise<Object>}
 */
export const login = async ({ email, password }) => {
  return client.request(customerAccessTokenCreateMutation, {
    input: {
      email,
      password,
    },
  });
};

/**
 * Logs out the current Shopify customer and provides a callback function
 *
 * @param {Function} callback
 */
export const logout = (callback) => {
  setUser({});
  callback();
};

/**
 * Returns a Shopify customer if that user is logged in with an access token
 *
 * @returns {Promise<Object>}
 */
export const getCustomer = async () => {
  if (!isLoggedIn()) return;

  return client.request(customerQuery, {
    customerAccessToken: getUser().accessToken,
  });
};

/**
 * Recovers a customer's password
 *
 * @param {Object} param
 * @param {string} param.email
 * @returns {Promise<Object>}
 */
export const customerRecover = async ({ email }) => {
  return client.request(customerRecoverMutation, {
    email,
  });
};

/**
 * Resets a customer's password
 *
 * @param {Object} param
 * @param {string} param.id
 * @param {string} param.password
 * @param {string} param.resetToken
 * @returns {Promise<Object>}
 */
export const customerReset = async ({ id, password, resetToken }) => {
  return client.request(customerResetMutation, {
    id,
    input: {
      password,
      resetToken,
    },
  });
};

/**
 * Create a customer
 *
 * @param {Object} param
 * @param {string} param.email
 * @param {string} param.firstName
 * @param {string} param.lastName
 * @param {string} param.password
 * @returns {Promise<Object>}
 */
export const customerCreate = ({
  email,
  firstName = '',
  lastName = '',
  password,
}) => {
  return client.request(customerCreateMutation, {
    input: {
      email,
      firstName,
      lastName,
      password,
    },
  });
};

/**
 * Activates a customer
 *
 * @param {Object} param
 * @param {string} param.activationToken
 * @param {string} param.id
 * @param {string} param.password
 * @returns {Promise<Object>}
 */
export const customerActivate = async ({ activationToken, id, password }) => {
  return client.request(customerActivateMutation, {
    id,
    input: {
      activationToken,
      password,
    },
  });
};

/**
 * Create a customer's address
 *
 * @param {Object} param
 * @param {string} param.address1
 * @param {string} param.address2
 * @param {string} param.city
 * @param {string} param.country
 * @param {string} param.firstName
 * @param {string} param.lastName
 * @param {string} param.province
 * @param {string} param.zip
 * @returns {Promise<Object>}
 */
export const customerAddressCreate = async ({
  address1,
  address2,
  city,
  country,
  firstName,
  lastName,
  province,
  zip,
}) => {
  return client.request(customerAddressCreateMutation, {
    customerAccessToken: getUser().accessToken,
    address: {
      address1,
      address2,
      city,
      country,
      firstName,
      lastName,
      province,
      zip,
    },
  });
};

/**
 * Deletes an address for a customer
 *
 * @param {Object} param
 * @param {string} param.addressId
 * @returns {Promise<Object>}
 */
export const customerAddressDelete = async ({ addressId }) => {
  return client.request(customerAddressDeleteMutation, {
    customerAccessToken: getUser().accessToken,
    id: addressId,
  });
};

/**
 * Sets the default address for a customer
 *
 * @param {Object} param
 * @param {string} param.addressId
 * @returns {Promise<Object>}
 */
export const customerAddressSetDefault = async ({ addressId }) => {
  return client.request(customerAddressSetDefaultMutation, {
    customerAccessToken: getUser().accessToken,
    addressId: addressId,
  });
};

/**
 * Updates a customer's address
 *
 * @param {Object} param
 * @param {string} param.address1
 * @param {string} param.address2
 * @param {string} param.city
 * @param {string} param.country
 * @param {string} param.firstName
 * @param {string} param.id
 * @param {string} param.lastName
 * @param {string} param.province
 * @param {string} param.zip
 * @returns {Promise<Object>}
 */
export const customerAddressUpdate = async ({
  address1,
  address2,
  city,
  country,
  firstName,
  id,
  lastName,
  province,
  zip,
}) => {
  return client.request(customerAddressUpdateMutation, {
    customerAccessToken: getUser().accessToken,
    id,
    address: {
      address1,
      address2,
      city,
      country,
      firstName,
      lastName,
      province,
      zip,
    },
  });
};

/**
 * Returns a Shopify error message by using an error code and message. Rate limit errors don't have a code, so parse the
 * message instead.
 *
 * @param {Object} error
 * @param {string} error.code
 * @param {string} error.message
 * @returns {string}
 */
export const getError = (error = {}) => {
  if (!error.code && !error.message) return '';

  if (!error.code && error.message.toLowerCase().includes('limit')) {
    error.code = 'RATE_LIMITED';
  }

  switch (error.code) {
    case 'RATE_LIMITED':
      return 'Attempt limit has been exceeded. Please try again later.';
    case 'UNIDENTIFIED_CUSTOMER':
      return 'Incorrect email or password. Please register or if you already have an account, recover your password.';
    case 'BLANK':
      return 'An input is blank.';
    case 'CONTAINS_HTML_TAGS':
      return 'Inputs cannot contain HTML tags.';
    case 'CONTAINS_URL':
      return 'Inputs cannot contain URLs.';
    case 'CUSTOMER_DISABLED':
      return 'Your account has been created. Please check your email for an account activation link.';
    case 'INVALID':
      return 'An input is invalid.';
    case 'NOT_FOUND':
      return 'This address does not exist.';
    case 'PASSWORD_STARTS_OR_ENDS_WITH_WHITESPACE':
      return 'Passwords cannot start or end with whitespace.';
    case 'TAKEN':
      return 'This email is already taken. Please log in instead or contact us to enable this account.';
    case 'TOKEN_INVALID':
      return 'The activation token is invalid or your new password may not meet the requirements.';
    case 'TOO_LONG':
      return 'An input value is too long.';
    case 'TOO_SHORT':
      return 'An input value is too short.';
    case 'ALREADY_ENABLED':
      return 'This account has already been activated.';
    default:
      return 'An error occurred. Please try again later.';
  }
};
